import React, { useState, useEffect } from "react";
import AdditionalInfoForm from "./AdditionalInfoForm";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../store";
import { idfServiceIds, ezsServiceIds } from "../../../../config/UserServicesList";
import { useAreServicesActive } from "../../../../util/CommonServices";
import { MonitoringData } from "../../../../store/common";
import * as services from "../../../../store/service";
import { isIDFPartnerUser } from "../../../../util/partner";
import { useTranslation } from "react-i18next";
import RenderIcons from "../../../../Assets/css/_icons/partner_icon/RenderIcons";

const AdditionalInfo: React.FunctionComponent<{ monitoringData: MonitoringData }> = (props) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();

    const [showForm, setShowForm] = useState(false);
    const [personalAssets, setPersonalAssets] = useState({});
    const [info, setInfo]: any = useState({});
    const [uiRendering, setUIRendering]: any = useState(false);

    const [isLoading, setisLoading] = useState(false);
    const [monitoringData, setMonitoringData] = useState(props.monitoringData);

    const isUpdatingAdditionalInfo = useSelector((state: ApplicationState) => (state.monitoringState ? state.monitoringState.isUpdatingAdditionalInfo : false));
    // For IDF partner checking if the monitoring for the different fields is active or not.
    const isDarkWebMonitoringActive = useAreServicesActive([ezsServiceIds.businessDarkWebMonitoring, ezsServiceIds.darkWebMonitoring]);
    const passportMonitoringServiceActive = useAreServicesActive([idfServiceIds.passportMonitoring]);
    const medicalIdMonitoringServiceActive = useAreServicesActive([idfServiceIds.medicalAccountMonitoring]);
    const motherMaidenNameMonitoringServiceActive = useAreServicesActive([idfServiceIds.mothersMaidenNameMonitoring]);
    const driverLicenseMonitoringServiceActive = useAreServicesActive([idfServiceIds.driversLicenseMonitoring]);
    const isIDFPartner = useSelector((state: ApplicationState) =>
        state.configurationState &&
        state.configurationState.configuration.partnerConfig.partnerId &&
        state.configurationState.configuration.partnerConfig.isIdfPartner
            ? !isIDFPartnerUser(state.configurationState.configuration.partnerConfig.partnerId)
            : false
    );

    const isPassposrtMonitoringActive = isDarkWebMonitoringActive ? true : passportMonitoringServiceActive;
    const isMedicalIdMonitoringActive = isDarkWebMonitoringActive ? true : medicalIdMonitoringServiceActive;
    const isMotherMaidenNameActive = isDarkWebMonitoringActive ? true : motherMaidenNameMonitoringServiceActive;
    const isDriversLicenseActive = isDarkWebMonitoringActive ? true : driverLicenseMonitoringServiceActive;

    const ensureDataFetched = () => {
        setisLoading(true);

        services
            .getMonitoringAfterUpdate(isIDFPartner ? {} : { asset: { additionalInformation: {} } })
            .then((getMonitoringResponse) => {
                setisLoading(false);
                setMonitoringData({ asset: getMonitoringResponse.asset });
            })
            .catch(function (error) {
                setisLoading(false);
            });
    };

    useEffect(() => {
        setUIRendering(true);
        setInfo(monitoringData);
        setPersonalAssets({ personalInfo: monitoringData.asset.personalInformation, addressInfo: monitoringData.asset.addressInformation });

        setDataOnForm(monitoringData.asset.additionalInformation);

        setTimeout(() => {
            setUIRendering(false);
        }, 500);
    }, [monitoringData]);

    const setDataOnForm = (data: any) => {
        setFormData({
            "ezshield-id": info && info["ezshield-id"],
            "idf-user-id": info && info["idf-user-id"],
            driversLicence: data && data["drivers-licence"] && data["drivers-licence"]["is-monitored"] ? data["drivers-licence"] : {},
            passportPrimary: data && data["passport-primary"] && data["passport-primary"]["is-monitored"] ? data["passport-primary"] : { passport: "" },
            additionlPassports: data && data["additional-passports"] ? data["additional-passports"].filter((passport: any) => passport["is-monitored"]) : [],
            medicalInsurance: data && data["medical-insurance"] && data["medical-insurance"]["is-monitored"] ? data["medical-insurance"] : { "medical-id": "" },
            additionalMedicalInsurance:
                data && data["additional-medicalids"] ? data["additional-medicalids"].filter((medicalId: any) => medicalId["is-monitored"]) : [],
            motherMaidenName: data && data["mother-maiden-name"] ? data["mother-maiden-name"] : {},
        });
    };

    const [formData, setFormData] = useState({
        driversLicence: { "driver-licence": "" },
        passportPrimary: { passport: "" },
        additionlPassports: [{ passport: "" }],
        medicalInsurance: { "medical-id": "" },
        additionalMedicalInsurance: [{ "medical-id": "" }],
        motherMaidenName: { "mother-maiden": "" },
        "ezshield-id": "",
        "idf-user-id": "",
    });

    return (
        <div className={`box offset-padding ${(isLoading || uiRendering || isUpdatingAdditionalInfo) && "loading"}`}>
            <div className="box-header">
                <div className="box-title-container">
                    <div className="box-title-icon" aria-hidden="true">
                        <RenderIcons iconName="info-circle" />
                    </div>
                    <div className="box-title-text has-actions">
                        <h2 className="box-title ">{tLang("additionalInformationWidget.title")}</h2>
                    </div>
                </div>
                {!showForm && (
                    <div className="box-actions" onClick={() => setShowForm(true)}>
                        <button type="button" className="btn-default btn-grey-dot" id="monitored-info-additional-info">
                            <span className="btn-text">
                                {tLang("additionalInformationWidget.editButtonText")} <span className="sr-only">{tLang("additionalInformationWidget.title")}</span>
                            </span>
                            <span className="btn-icon">
                                <RenderIcons iconName="pen" />
                            </span>
                        </button>
                    </div>
                )}
            </div>
            <div className="box-content">
                {/* <!-- Grid Content --> */}
                {uiRendering || !showForm ? (
                    <div role="grid" className="data-grid " id="monitored-info-additional-info-grid" aria-hidden="false">
                        {isDriversLicenseActive && (
                            <div role="row" className="data-row row">
                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div role="gridcell" className="data-label" id="label-drivers-license-display">
                                        {tLang("additionalInformationWidget.label.dl")}
                                    </div>
                                </div>
                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                    <div role="gridcell" className="data-value" aria-labelledby="label-drivers-license-display">
                                        {formData.driversLicence && formData.driversLicence["driver-licence"]
                                            ? "*".repeat(
                                                  formData.driversLicence["driver-licence"].substring(0, formData.driversLicence["driver-licence"].length - 4)
                                                      .length
                                              ) + formData.driversLicence["driver-licence"].substring(formData.driversLicence["driver-licence"].length - 4)
                                            : "-"}
                                    </div>
                                </div>
                            </div>
                        )}

                        {isPassposrtMonitoringActive && formData.passportPrimary && formData.passportPrimary.passport
                            ? [formData.passportPrimary, ...formData.additionlPassports].map((passport: any, index: number) => {
                                  return (
                                      <div key={index} role="row" className="data-row row">
                                          <div className="col-xs-12 col-sm-4 col-no-gutter">
                                              <div role="gridcell" className="data-label" id={`label-passport-display-${index}`}>
                                                  {index === 0 && tLang("additionalInformationWidget.label.passport")}
                                              </div>
                                          </div>
                                          <div className="col-xs-12 col-sm-8 col-no-gutter">
                                              <div role="gridcell" className="data-value" aria-labelledby={`label-passport-display-${index}`}>
                                                  {passport &&
                                                      passport.passport &&
                                                      `${"*".repeat(
                                                          passport.passport.substring(0, passport.passport.length - 4).length
                                                      )}${passport.passport.substring(passport.passport.length - 4)}`}
                                              </div>
                                          </div>
                                      </div>
                                  );
                              })
                            : formData && formData.additionlPassports && formData.additionlPassports.length
                            ? formData.additionlPassports.map((passport: any, index: number) => {
                                  return (
                                      <div key={index} role="row" className="data-row row">
                                          <div className="col-xs-12 col-sm-4 col-no-gutter">
                                              <div role="gridcell" className="data-label" id={`label-passport-display-${index}`}>
                                                  {index === 0 && tLang("additionalInformationWidget.label.passport")}
                                              </div>
                                          </div>
                                          <div className="col-xs-12 col-sm-8 col-no-gutter">
                                              <div role="gridcell" className="data-value" aria-labelledby={`label-passport-display-${index}`}>
                                                  {passport &&
                                                      passport.passport &&
                                                      `${"*".repeat(
                                                          passport.passport.substring(0, passport.passport.length - 4).length
                                                      )}${passport.passport.substring(passport.passport.length - 4)}`}
                                              </div>
                                          </div>
                                      </div>
                                  );
                              })
                            : isPassposrtMonitoringActive && (
                                  <div role="row" className="data-row row">
                                      <div className="col-xs-12 col-sm-4 col-no-gutter">
                                          <div role="gridcell" className="data-label" id="label-passport-display">
                                              {tLang("additionalInformationWidget.label.passport")}
                                          </div>
                                      </div>
                                  </div>
                              )}

                        {isMedicalIdMonitoringActive && formData.medicalInsurance && formData.medicalInsurance["medical-id"]
                            ? [formData.medicalInsurance, ...formData.additionalMedicalInsurance].map((medicalId: any, index: number) => {
                                  return (
                                      <div key={index} role="row" className="data-row row">
                                          <div className="col-xs-12 col-sm-4 col-no-gutter">
                                              <div role="gridcell" className="data-label" id={`label-medical-insurance-display-${index}`}>
                                                  {index === 0 && tLang("additionalInformationWidget.label.medicalInsurance")}
                                              </div>
                                          </div>
                                          <div className="col-xs-12 col-sm-8 col-no-gutter">
                                              <div role="gridcell" className="data-value" aria-labelledby={`label-medical-insurance-display-${index}`}>
                                                  {medicalId && medicalId["medical-id"]
                                                      ? `${"*".repeat(medicalId["medical-id"].substring(0, medicalId["medical-id"].length - 4).length)}${medicalId[
                                                            "medical-id"
                                                        ].substring(medicalId["medical-id"].length - 4)}`
                                                      : ""}
                                              </div>
                                          </div>
                                      </div>
                                  );
                              })
                            : formData.additionalMedicalInsurance.length
                            ? formData.additionalMedicalInsurance.map((medicalId: any, index: number) => {
                                  return (
                                      <div key={index} role="row" className="data-row row">
                                          <div className="col-xs-12 col-sm-4 col-no-gutter">
                                              <div role="gridcell" className="data-label" id={`label-medical-insurance-display-${index}`}>
                                                  {index === 0 && tLang("additionalInformationWidget.label.medicalInsurance")}
                                              </div>
                                          </div>
                                          <div className="col-xs-12 col-sm-8 col-no-gutter">
                                              <div role="gridcell" className="data-value" aria-labelledby={`label-medical-insurance-display-${index}`}>
                                                  {medicalId && medicalId["medical-id"]
                                                      ? `${"*".repeat(medicalId["medical-id"].substring(0, medicalId["medical-id"].length - 4).length)}${medicalId[
                                                            "medical-id"
                                                        ].substring(medicalId["medical-id"].length - 4)}`
                                                      : ""}
                                              </div>
                                          </div>
                                      </div>
                                  );
                              })
                            : isMedicalIdMonitoringActive && (
                                  <div role="row" className="data-row row">
                                      <div className="col-xs-12 col-sm-4 col-no-gutter">
                                            <div role="gridcell" className="data-label" id="label-passport-display">
                                              {tLang("additionalInformationWidget.label.medicalInsurance")}
                                          </div>
                                      </div>
                                  </div>
                              )}

                        {isMotherMaidenNameActive && (
                            <div role="row" className="data-row row">
                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                    <div role="gridcell" className="data-label" id="label-mothers-maiden-name-display">
                                        {tLang("additionalInformationWidget.label.mothersMaidenName")}
                                    </div>
                                </div>
                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                    <div role="gridcell" className="data-value" aria-labelledby="label-mothers-maiden-name-display">
                                        {formData.motherMaidenName && formData.motherMaidenName["mother-maiden"]
                                            ? "*".repeat(
                                                  formData.motherMaidenName["mother-maiden"].substring(0, formData.motherMaidenName["mother-maiden"].length - 3)
                                                      .length
                                              ) + formData.motherMaidenName["mother-maiden"].substring(formData.motherMaidenName["mother-maiden"].length - 3)
                                            : "-"}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <AdditionalInfoForm
                        refreshDataCallback={ensureDataFetched}
                        setStatus={setShowForm}
                        formData={formData}
                        personalData={personalAssets}
                        activeStatus={{
                            isDriversLicenseActive,
                            isPassposrtMonitoringActive,
                            isMedicalIdMonitoringActive,
                            isMotherMaidenNameActive,
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default AdditionalInfo;
